<template>
  <NTab
    rounded="full"
    :title="title"
    :class="{
      '!bg-neutral-primary hover:!bg-neutral-primary-hover': !isActive,
    }"
  >
    <template #append>
      <span
        class="w-8 h-5 rounded-full flex items-center justify-center pt-1"
        :class="
          isActive
            ? 'bg-neutral-primary-hover text-brand-primary'
            : 'bg-neutral-secondary-hover text-neutral-secondary'
        "
      >
        {{ filteredOrders.length }}
      </span>
    </template>

    <Error v-if="isErrorLoadingOrders" @retry="emit('getOrders')" />

    <OrdersTable
      v-else-if="expandedAssets"
      :order-items="filteredOrders"
      :filter="filter"
      :offers-isin="offersIsin"
      :offers="initialPublicOffers"
      :loading-delete-order="
        isLoadingDeleteOrder || isLoadingDeleteOfflineOrder
      "
      @delete-order="deleteOrder"
      @delete-offline-order="deleteOfflineOrder"
    />
  </NTab>
</template>

<script setup lang="ts">
import { useToast } from '@negar/core';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import {
  useFilterOrders,
  useDeleteOrderQuery,
  useDeleteOfflineOrderQuery,
  ORDERS_QUERY_KEYS,
} from '@core/order';
import { useLiveInitialPublicOffers } from '@core/ipo';
import { OrderItem } from '@libs/api-reference';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';

import { ORDER_EXECUTION_TYPE, HELIUM_DEVICE_TYPE } from '@/modules/order';

import Error from '@/components/Error.vue';

import OrdersTable from './OrdersTable.vue';

type OrdersTableTabProps = {
  expandedAssets: boolean;
  filter: string;
  isActive: boolean;
  title: string;
  orders: OrderItem[];
  isErrorLoadingOrders: boolean;
};

type OrdersTableTabEmits = {
  (e: 'getOrders'): void;
};

const props = defineProps<OrdersTableTabProps>();
const emit = defineEmits<OrdersTableTabEmits>();

const orders = computed(() => props.orders);

const toast = useToast();
const { t } = useI18n();
const queryClient = useQueryClient();

const { orders: filteredOrders } = useFilterOrders(orders, props.filter);

const { offersIsin, initialPublicOffers } = useLiveInitialPublicOffers();

const { deleteOrder, isLoadingDeleteOrder } = useDeleteOrderQuery({
  deviceType: HELIUM_DEVICE_TYPE,
  orderExecutionType: ORDER_EXECUTION_TYPE,
  onSuccess(result, { closeDeleteOrderDialog }) {
    toast.neutral({
      message: t('modules.order.cancelOrderMessage'),
      duration: 3000,
    });

    closeDeleteOrderDialog();
  },
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({
          message,
          duration: 3000,
        });
      }
    }
  },
});

const { deleteOfflineOrder, isLoadingDeleteOfflineOrder } =
  useDeleteOfflineOrderQuery({
    async onSuccess(result, { closeDeleteOrderDialog }) {
      toast.neutral({
        message: t('modules.order.cancelOrderMessage'),
        duration: 3000,
      });

      await sleep(1000);

      queryClient.invalidateQueries(ORDERS_QUERY_KEYS.orders);

      closeDeleteOrderDialog();
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });
</script>
